import { REPORT_SPA_QUERY_PARAMS } from "@core/reports/constants/reportSpaQueryParams";
import {
  queryKeysForLens,
  type ReportEntityScope,
  type ReportLensKey,
} from "@core/reports/utils/reportSectionOrigin";

export const REPORT_SECTION_VIEWS = [
  "default",
  "by_month",
  "by_week",
  "on_time",
  "late",
  "outstanding",
  "custom_window",
  "timeline",
] as const;

export type ReportSectionView = (typeof REPORT_SECTION_VIEWS)[number];

/** Views a named presentation layout cannot show; the API treats them as Default (ReportSectionViewLens::LAYOUT_EXCLUDED_VIEWS). */
export const LAYOUT_EXCLUDED_VIEWS: readonly ReportSectionView[] = ["outstanding", "timeline"];

export type ReportSectionViewLensParams = {
  view: ReportSectionView;
  bucket?: string;
  from?: string;
  to?: string;
};

export type SectionLensKey = ReportLensKey;

/** Query param for the active template tab (`church` | `district` | `nation`). */
export const REPORT_SECTION_TAB_PARAM = REPORT_SPA_QUERY_PARAMS.SECTION;

/**
 * Active tab from the URL. Uses `section` when it is in `allowedTypes`, otherwise the first allowed type.
 * Returns `""` when `allowedTypes` is empty.
 */
export function readActiveReportSection(
  search: URLSearchParams,
  allowedTypes: string[]
): string {
  if (allowedTypes.length === 0) return "";
  const raw = search.get(REPORT_SECTION_TAB_PARAM);
  if (raw && allowedTypes.includes(raw)) return raw;
  return allowedTypes[0];
}

/**
 * Persist the active tab. Sets `section` when selecting a non-default tab;
 * deletes it when selecting the first allowed type so default links stay clean.
 * Invalid `sectionType` values are ignored.
 */
export function writeActiveReportSection(
  search: URLSearchParams,
  sectionType: string,
  allowedTypes: string[]
): void {
  if (allowedTypes.length === 0 || !allowedTypes.includes(sectionType)) return;
  if (sectionType === allowedTypes[0]) {
    search.delete(REPORT_SECTION_TAB_PARAM);
    return;
  }
  search.set(REPORT_SECTION_TAB_PARAM, sectionType);
}

export function parseReportSectionView(value: string | null | undefined): ReportSectionView {
  const v = (value ?? "default").toLowerCase();
  return (REPORT_SECTION_VIEWS as readonly string[]).includes(v)
    ? (v as ReportSectionView)
    : "default";
}

export function readSectionLens(
  search: URLSearchParams,
  section: SectionLensKey,
  scope: ReportEntityScope = "church"
): ReportSectionViewLensParams {
  const keys = queryKeysForLens(scope, section);
  return {
    view: parseReportSectionView(search.get(keys.view)),
    bucket: search.get(keys.bucket) || undefined,
    from: search.get(keys.from) || undefined,
    to: search.get(keys.to) || undefined,
  };
}

export function writeSectionLens(
  search: URLSearchParams,
  section: SectionLensKey,
  lens: ReportSectionViewLensParams,
  scope: ReportEntityScope = "church"
): void {
  const setOrDelete = (key: string, value?: string) => {
    if (value) search.set(key, value);
    else search.delete(key);
  };
  const keys = queryKeysForLens(scope, section);
  setOrDelete(keys.view, lens.view === "default" ? undefined : lens.view);
  setOrDelete(keys.bucket, lens.bucket);
  setOrDelete(keys.from, lens.from);
  setOrDelete(keys.to, lens.to);
  search.set(keys.page, "1");
}

export function lensToApiParams(
  section: SectionLensKey,
  lens: ReportSectionViewLensParams,
  scope: ReportEntityScope = "church"
): Record<string, string> {
  const keys = queryKeysForLens(scope, section);
  const out: Record<string, string> = {};
  if (lens.view && lens.view !== "default") out[keys.view] = lens.view;
  if (lens.bucket) out[keys.bucket] = lens.bucket;
  if (lens.from) out[keys.from] = lens.from;
  if (lens.to) out[keys.to] = lens.to;
  return out;
}

/** View params for one tab. The body request sends only the active tab. */
export function collectLensApiParams(
  search: URLSearchParams,
  section: SectionLensKey,
  scope: ReportEntityScope = "church"
): Record<string, string> {
  return lensToApiParams(section, readSectionLens(search, section, scope), scope);
}

function formatOneLensToken(section: SectionLensKey, lens: ReportSectionViewLensParams): string | null {
  if (!lens.view || lens.view === "default") return null;
  const parts: string[] = [section, lens.view];
  if (lens.bucket) parts.push(lens.bucket);
  else if (lens.from || lens.to) {
    parts.push([lens.from ?? "", lens.to ?? ""].filter(Boolean).join("_"));
  }
  return parts.join(":");
}

/** Human-readable subtitle of non-default section lenses for PDF header. */
function lensKeysForScope(scope: ReportEntityScope): SectionLensKey[] {
  if (scope === "church") return ["church"];
  if (scope === "district") return ["children", "office"];
  return ["children", "district_origin", "office"];
}

export function formatActiveLensesSubtitle(
  search: URLSearchParams,
  scope: ReportEntityScope = "district"
): string | null {
  const tokens = lensKeysForScope(scope)
    .map((section) => formatOneLensToken(section, readSectionLens(search, section, scope)))
    .filter((t): t is string => Boolean(t));
  if (tokens.length === 0) return null;
  return `Views: ${tokens.join("; ")}`;
}

/** Filename-safe suffix for non-default lenses (no extension). */
export function formatActiveLensesFileSuffix(
  search: URLSearchParams,
  scope: ReportEntityScope = "district"
): string | null {
  const tokens = lensKeysForScope(scope)
    .map((section) => formatOneLensToken(section, readSectionLens(search, section, scope)))
    .filter((t): t is string => Boolean(t));
  if (tokens.length === 0) return null;
  return tokens
    .map((t) => t.replace(/:/g, "-"))
    .join(" ")
    .replace(/[^a-zA-Z0-9 \-_.]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function viewLabelForSection(
  view: ReportSectionView,
  sectionType: string
): string {
  if (view === "outstanding") {
    return sectionType === "children" || sectionType === "district_origin"
      ? "Not approved"
      : "Not submitted";
  }
  const labels: Record<ReportSectionView, string> = {
    default: "Default",
    by_month: "By month",
    by_week: "By week",
    on_time: "On time",
    late: "Late",
    outstanding: "Not submitted",
    custom_window: "Custom date window",
    timeline: "Timeline",
  };
  return labels[view];
}

/** Sunday YYYY-MM-DD in UTC for the week containing now (or a given date). */
export function currentUtcSunday(date = new Date()): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.toISOString().slice(0, 10);
}

export function currentUtcMonth(date = new Date()): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Inclusive UTC datetime bounds for a calendar month (`YYYY-MM-DD HH:mm:ss`). */
export function utcMonthBounds(date = new Date()): { from: string; to: string } {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (d: Date) =>
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  const start = new Date(Date.UTC(y, m, 1, 0, 0, 0));
  const end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59));
  return { from: fmt(start), to: fmt(end) };
}

export function weekOfLabel(sundayYmd: string): string {
  const start = new Date(`${sundayYmd}T00:00:00Z`);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  return `Week of ${fmt(start)}–${fmt(end)}`;
}
